/**
 * Блок оценки криптоактивов (карточка BL-2026-08-15-c88c).
 *
 * Flow: опрос /api/treasury/crypto-valuation раз в 60 секунд → итог в долларах
 *       на конец суток платёжного контура → два расхождения рядом с ним →
 *       ввод ручного курса для валюты, которой курса не хватило.
 *
 * Главное свойство блока: величина, которую не удалось оценить, НЕ показывается
 * нулём. Кошелёк без курса виден со своим остатком в своей валюте, а итог
 * помечен неполным с указанием причины (BR-010, BR-011, BR-023).
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadCryptoValuation, createManualFxRate.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает блок на экране;
 *   - docs/specs/BL-2026-08-15-c88c-crypto-valuation.md — техспека.
 */

import { useCallback, useEffect, useState } from 'react'

import { createManualFxRate, loadCryptoValuation } from './api'

/** Период опроса. Срез собирается раз в час — чаще смотреть незачем. */
const POLL_MS = 60000

/** Календарь суток платёжного контура: границы по полуночи UTC. */
const CALENDAR = 'psp'

/**
 * Показывает денежную величину.
 *
 * Пустое значение — ПРОЧЕРК, а не ноль: «оценки нет» и «оценка равна нулю» —
 * разные утверждения, и второе скрыло бы первое (BR-011).
 */
export function formatUsd(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Показывает остаток в своей валюте: у крипты значащие знаки глубже двух. */
export function formatCoin(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 8 })
}

/**
 * Собирает валюты, которым не хватило курса.
 *
 * Нужны затем, чтобы форма ввода предлагала ровно то, чего не хватает,
 * а не весь справочник валют.
 */
export function unpricedCurrencies(wallets) {
  const out = []
  for (const w of wallets || []) {
    if (w.amount_usd === null || w.amount_usd === undefined) {
      if (!out.includes(w.currency_code)) out.push(w.currency_code)
    }
  }
  return out
}

/**
 * Показывает разность ручной и машинной величины со знаком.
 *
 * Знак сохраняется: «ручная выше машинной» и «ниже» разбираются по-разному,
 * и модуль стёр бы это различие.
 */
export function formatDiff(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return value
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function CryptoValuationPanel({ user }) {
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')
  const [form, setForm] = useState({ currency_code: '', trade_date: '', rate: '', comment: '' })
  const [formError, setFormError] = useState('')
  const [formOk, setFormOk] = useState('')

  const load = useCallback(() => {
    loadCryptoValuation()
      .then((data) => {
        setReport(data)
        setError('')
      })
      .catch((e) => setError(e.message))
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  if (error) {
    return (
      <section className="card">
        <h2>Оценка криптоактивов</h2>
        <div className="error">{error}</div>
      </section>
    )
  }

  if (!report) {
    return (
      <section className="card">
        <h2>Оценка криптоактивов</h2>
        <p className="muted">Загрузка…</p>
      </section>
    )
  }

  // Пока суточных срезов платёжного контура нет вовсе, показывать нечего —
  // но и молчать нельзя: пустой блок неотличим от «всё оценено».
  if (!report.trade_date) {
    return (
      <section className="card">
        <h2>Оценка криптоактивов</h2>
        <p className="muted">
          Суточных срезов платёжного контура ещё нет: оценка на конец суток появится
          после первых закрытых суток.
        </p>
      </section>
    )
  }

  const wallets = report.wallets || []
  const unpriced = unpricedCurrencies(wallets)
  const canWrite = user && (user.role === 'admin' || user.role === 'backoffice')

  const submit = (e) => {
    e.preventDefault()
    setFormError('')
    setFormOk('')
    createManualFxRate({ ...form, calendar_code: CALENDAR })
      .then((res) => {
        setFormOk(`Курс принят, строк оценено: ${res.converted_rows}`)
        setForm({ currency_code: '', trade_date: '', rate: '', comment: '' })
        load()
      })
      .catch((err) => setFormError(err.message))
  }

  return (
    <section className="card">
      <h2>Оценка криптоактивов</h2>

      <p className="muted">
        Конец суток {report.trade_date} по календарю платёжного контура (UTC).
        Это ЗАФИКСИРОВАННЫЙ срез: он не меняется в течение дня и потому
        сопоставим с другими днями. Сколько на кошельках сейчас — в блоке
        «Общий баланс PSP».
      </p>

      <table>
        <tbody>
          <tr>
            {/* «На конец суток» стоит в самой подписи, а не только в шапке
                блока: на экране есть вторая долларовая величина — живой
                остаток в «Общем балансе PSP», — и заказчик принял одну
                за другую. Две суммы об одних и тех же деньгах обязаны
                различаться НАЗВАНИЕМ, а не только соседним пояснением. */}
            <td>Оценено в USD на конец суток</td>
            <td className="num">{formatUsd(report.total_usd)}</td>
          </tr>
          {report.manual_entry_usd !== null && report.manual_entry_usd !== undefined && (
            <tr>
              <td>Ручная строка «Крипто-кошелёк»</td>
              <td className="num">{formatUsd(report.manual_entry_usd)}</td>
            </tr>
          )}
          {report.manual_entry_diff !== null && report.manual_entry_diff !== undefined && (
            <tr>
              <td>Расхождение с ручной строкой</td>
              <td className="num">{formatDiff(report.manual_entry_diff)}</td>
            </tr>
          )}
        </tbody>
      </table>

      {!report.is_complete && (
        <div className="warning">
          <strong>Показатель неполон.</strong>
          <ul>
            {(report.reasons || []).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Перечень оценённых кошельков — в растягивающейся области:
          блок выровнен по соседу в ряду, и лишняя высота достаётся
          перечню, а не пустоте под ним. */}
      <div className="card-fill">
      <table>
        <thead>
          <tr>
            <th>Система</th>
            <th>Кошелёк</th>
            <th>Валюта</th>
            <th className="num">Остаток</th>
            <th className="num">Курс</th>
            <th>Источник курса</th>
            <th className="num">Оценка, USD</th>
          </tr>
        </thead>
        <tbody>
          {wallets.map((w) => (
            <tr key={`${w.source_code}|${w.external_id}`}>
              <td>{w.source_code}</td>
              <td>{w.title || w.external_id}</td>
              <td>{w.currency_code}</td>
              <td className="num">{formatCoin(w.balance)}</td>
              <td className="num">{formatCoin(w.fx_rate)}</td>
              <td>{w.fx_rate_source || 'курса нет'}</td>
              <td className="num">{formatUsd(w.amount_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {(report.rate_divergences || []).length > 0 && (
        <>
          <h3>Расхождение с курсом платёжной системы</h3>
          <p className="muted">
            Провайдер сообщает курс валюты платежа к валюте кошелька, а не к доллару.
            Сравнивается кросс-курс: его против нашего за те же сутки.
          </p>
          <table>
            <thead>
              <tr>
                <th>Система</th>
                <th>Операция</th>
                <th>Пара</th>
                <th className="num">Курс провайдера</th>
                <th className="num">Наш курс</th>
                <th className="num">Отклонение, %</th>
              </tr>
            </thead>
            <tbody>
              {report.rate_divergences.map((d) => (
                <tr key={`${d.source_code}|${d.external_id}`}>
                  <td>{d.source_code}</td>
                  <td>{d.external_id}</td>
                  <td>
                    {d.pay_currency_code}→{d.wallet_currency_code}
                  </td>
                  <td className="num">{formatCoin(d.provider_rate)}</td>
                  <td className="num">{formatCoin(d.our_rate)}</td>
                  <td className="num">{formatDiff(d.deviation_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      </div>

      {/* Форма ввода курса — СНАРУЖИ прокрутки: уехав вместе с таблицей,
          она перестала бы попадаться на глаза тому, ради кого заведена. */}
      {canWrite && unpriced.length > 0 && (
        <>
          <h3>Ввести курс закрытия</h3>
          <p className="muted">
            Ручной курс выигрывает у котировки источника за те же сутки. Записанный
            курс не переписывается: ошибку исправляют вводом за следующий день.
          </p>
          <form onSubmit={submit}>
            <select
              value={form.currency_code}
              onChange={(e) => setForm({ ...form, currency_code: e.target.value })}
              required
            >
              <option value="">валюта</option>
              {unpriced.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={form.trade_date}
              onChange={(e) => setForm({ ...form, trade_date: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="курс к USD"
              value={form.rate}
              onChange={(e) => setForm({ ...form, rate: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="чем объяснён курс"
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              required
            />
            <button type="submit">Записать</button>
          </form>
          {formError && <div className="error">{formError}</div>}
          {formOk && <div className="muted">{formOk}</div>}
        </>
      )}
    </section>
  )
}
